import { createTheme } from '@mui/material/styles';

import { colorPurpose, errorRed, infoBlue, neutralColors, primaryBrand, secondaryBrand, successGreen, warningYellow } from './colors';
import { spacingConfig } from './spacing';
import { typography } from './typography';

// Extend the theme with custom design system colors, typography, and spacing
declare module '@mui/material/styles' {
  interface Theme {
    hhglobal: {
      neutral: typeof neutralColors;
      utility: {
        info: typeof infoBlue;
        success: typeof successGreen;
        warning: typeof warningYellow;
        error: typeof errorRed;
      };
      brand: {
        primary: typeof primaryBrand;
        secondary: typeof secondaryBrand;
      };
      purpose: typeof colorPurpose;
      typography: typeof typography;
      spacing: typeof spacingConfig;
    };
  }

  interface ThemeOptions {
    hhglobal?: {
      neutral?: typeof neutralColors;
      utility?: {
        info?: typeof infoBlue;
        success?: typeof successGreen;
        warning?: typeof warningYellow;
        error?: typeof errorRed;
      };
      brand?: {
        primary?: typeof primaryBrand;
        secondary?: typeof secondaryBrand;
      };
      purpose?: typeof colorPurpose;
      typography?: typeof typography;
      spacing?: typeof spacingConfig;
    };
  }
}

/**
 * HHGlobal Design System Material UI Theme
 * Integrates the HHGlobal design system colors with Material UI's theming system.
 */
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryBrand.emeraldGreen,
      dark: primaryBrand.impactGreen,
      contrastText: neutralColors.superblack,
    },
    secondary: {
      main: secondaryBrand.deepBlue,
      light: secondaryBrand.skyBlue,
      contrastText: '#FFFFFF',
    },
    error: {
      main: errorRed[800],
      dark: errorRed[900],
      light: errorRed[100],
      contrastText: '#FFFFFF',
    },
    warning: {
      main: warningYellow[800],
      dark: warningYellow[900],
      light: warningYellow[100],
      contrastText: neutralColors.superblack,
    },
    info: {
      main: infoBlue[800],
      dark: infoBlue[900],
      light: infoBlue[100],
      contrastText: '#FFFFFF',
    },
    success: {
      main: successGreen[800],
      dark: successGreen[900],
      light: successGreen[100],
      contrastText: '#FFFFFF',
    },
    text: {
      primary: colorPurpose.textStrong,
      secondary: colorPurpose.textWeak,
      disabled: neutralColors.gray300,
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    divider: colorPurpose.strokeWeak,
    action: {
      active: neutralColors.gray700,
      hover: '#0000000a',
      selected: neutralColors.gray100,
      disabled: neutralColors.gray300,
      disabledBackground: neutralColors.gray50,
    },
  },
  typography: {
    fontFamily: [
      typography.typefaces.primary,
      typography.typefaces.fallback,
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: typography.headings.h1.fontSizeRem,
      lineHeight: typography.headings.h1.lineHeightRem,
      fontWeight: typography.headings.h1.fontWeight,
      color: colorPurpose.textStrong,
    },
    h2: {
      fontSize: typography.headings.h2.fontSizeRem,
      lineHeight: typography.headings.h2.lineHeightRem,
      fontWeight: typography.headings.h2.fontWeight,
      color: colorPurpose.textStrong,
    },
    h3: {
      fontSize: typography.headings.h3.fontSizeRem,
      lineHeight: typography.headings.h3.lineHeightRem,
      fontWeight: typography.headings.h3.fontWeight,
      color: colorPurpose.textStrong,
    },
    h4: {
      fontSize: typography.headings.h4.fontSizeRem,
      lineHeight: typography.headings.h4.lineHeightRem,
      fontWeight: typography.headings.h4.fontWeight,
      color: colorPurpose.textStrong,
    },
    h5: {
      fontSize: typography.headings.h4.fontSizeRem,
      lineHeight: typography.headings.h4.lineHeightRem,
      fontWeight: typography.headings.h4.fontWeight,
      color: colorPurpose.textStrong,
    },
    h6: {
      fontSize: typography.headings.h4.fontSizeRem,
      lineHeight: typography.headings.h4.lineHeightRem,
      fontWeight: typography.headings.h4.fontWeight,
      color: colorPurpose.textStrong,
    },
    body1: {
      fontSize: typography.body.base.fontSizeRem,
      lineHeight: typography.body.base.lineHeightRem,
      fontWeight: typography.body.base.fontWeight,
      color: colorPurpose.textStrong,
    },
    body2: {
      fontSize: typography.body.small.fontSizeRem,
      lineHeight: typography.body.small.lineHeightRem,
      fontWeight: typography.body.small.fontWeight,
      color: colorPurpose.textWeak,
    },
    button: {
      textTransform: 'none',
      fontWeight: typography.fontWeights.regular,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '8px 16px',
        },
        contained: {
          backgroundColor: colorPurpose.buttonStrong,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: neutralColors.gray700,
          },
        },
        outlined: {
          borderColor: colorPurpose.strokeStrong,
          color: colorPurpose.textStrong,
          '&:hover': {
            borderColor: colorPurpose.strokeStrong,
            backgroundColor: colorPurpose.fillWeak,
          },
        },
        text: {
          color: colorPurpose.textStrong,
          '&:hover': {
            backgroundColor: colorPurpose.fillWeak,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colorPurpose.strokeStrong,
            },
            '&:hover fieldset': {
              borderColor: colorPurpose.strokeStrong,
            },
            '&.Mui-focused fieldset': {
              borderColor: colorPurpose.strokeStrong,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colorPurpose.fillStrong,
          color: colorPurpose.textStrong,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        colorInfo: {
          backgroundColor: colorPurpose.info.fill,
          color: colorPurpose.info.text,
          '& .MuiAlert-icon': {
            color: colorPurpose.info.text,
          },
        },
        colorSuccess: {
          backgroundColor: colorPurpose.success.fill,
          color: colorPurpose.success.text,
          '& .MuiAlert-icon': {
            color: colorPurpose.success.text,
          },
        },
        colorWarning: {
          backgroundColor: colorPurpose.warning.fill,
          color: colorPurpose.warning.text,
          '& .MuiAlert-icon': {
            color: colorPurpose.warning.text,
          },
        },
        colorError: {
          backgroundColor: colorPurpose.error.fill,
          color: colorPurpose.error.text,
          '& .MuiAlert-icon': {
            color: colorPurpose.error.text,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: colorPurpose.fillWeak,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colorPurpose.strokeWeak}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td, &:last-child th': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: neutralColors.gray400 + '!important',
            '&:hover': {
              backgroundColor: neutralColors.gray400,
            },
          },
        },
      },
    },
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
  },
  // Add custom design system colors, typography, and spacing to the theme
  hhglobal: {
    neutral: neutralColors,
    utility: {
      info: infoBlue,
      success: successGreen,
      warning: warningYellow,
      error: errorRed,
    },
    brand: {
      primary: primaryBrand,
      secondary: secondaryBrand,
    },
    purpose: colorPurpose,
    typography,
    spacing: spacingConfig,
  },
});

export const hhglobalTheme = theme;
export default theme;
